'use client';

import { useEffect, useState } from 'react';

// NOTE: These variables are client-only and modified exclusively within window event listeners.
// They do not leak across server requests because they are never executed or mutated during SSR.
let sharedTriggered =
  typeof window !== 'undefined' && !!(window as any).__pre_hydration_interacted;
const subscribers = new Set<() => void>();
let listenersInitialized = false;

function fireAll(e?: Event) {
  if (sharedTriggered) return;
  sharedTriggered = true;

  const runNotifications = () => {
    subscribers.forEach((notify) => notify());
    subscribers.clear();
  };

  if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
    window.requestIdleCallback(runNotifications, { timeout: 500 });
  } else {
    setTimeout(runNotifications, 0);
  }

  teardownListeners();
}

function handleScroll(e: Event) {
  if (e.isTrusted && typeof window !== 'undefined' && window.scrollY > 10) {
    fireAll(e);
  }
}

function handleClick(e: Event) {
  if (e.isTrusted) {
    fireAll(e);
  }
}

function handleKeydown(e: Event) {
  if (e.isTrusted) {
    fireAll(e);
  }
}

function handleTouchstart(e: Event) {
  if (e.isTrusted) {
    fireAll(e);
  }
}

function teardownListeners() {
  if (typeof window === 'undefined') return;
  window.removeEventListener('scroll', handleScroll);
  window.removeEventListener('click', handleClick);
  window.removeEventListener('keydown', handleKeydown);
  window.removeEventListener('touchstart', handleTouchstart);
  window.removeEventListener('pre-hydration-interaction', fireAll);
}

function initListenersOnce() {
  if (listenersInitialized || typeof window === 'undefined') return;
  listenersInitialized = true;

  if ((window as any).__pre_hydration_interacted) {
    fireAll();
    return;
  }

  window.addEventListener('pre-hydration-interaction', fireAll, { once: true });
  window.addEventListener('scroll', handleScroll, { passive: true });
  window.addEventListener('click', handleClick);
  window.addEventListener('keydown', handleKeydown);
  window.addEventListener('touchstart', handleTouchstart, { passive: true });
}

/**
 * Returns true once the visitor has interacted with the page in any of the
 * tracked ways.
 */
export function useDeferredScriptGate(): boolean {
  const [triggered, setTriggered] = useState(
    sharedTriggered ||
      (typeof window !== 'undefined' &&
        !!(window as any).__pre_hydration_interacted)
  );

  useEffect(() => {
    if (sharedTriggered) {
      setTriggered(true);
      return;
    }

    const notify = () => setTriggered(true);
    subscribers.add(notify);
    initListenersOnce();

    return () => {
      subscribers.delete(notify);
    };
  }, []);

  return triggered;
}
